import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

const STEPS = [
  {
    target: null,
    title: 'Welcome to Enpower Org DNA™',
    body: "The organizational intelligence platform built for Indian mid-market companies. In the next 2 minutes we'll show you exactly how to use it.",
    cta: 'Start the tour →',
    emoji: '🧬',
    isWelcome: true,
  },
  {
    target: 'tour-companies',
    title: 'Create your company workspace',
    body: 'Start here. Add your company and set up your diagnostic workspace. You can run multiple assessment rounds over time to track progress.',
    placement: 'right',
    step: '01',
  },
  {
    target: 'tour-respondents',
    title: 'Invite your team to the diagnostic',
    body: 'Share a unique survey link with your leadership, managers, and team. The 12-dimension assessment takes about 10 minutes and is fully anonymous.',
    placement: 'right',
    step: '02',
  },
  {
    target: 'tour-report',
    title: 'Your organizational health score',
    body: 'Once responses come in, your Org DNA report is generated instantly. Get dimension-by-dimension scores, critical insights, and a prioritized action roadmap.',
    placement: 'right',
    step: '03',
  },
  {
    target: 'tour-ai',
    title: 'Your AI consulting partner',
    body: 'Ask anything. Build RACI matrices, KPI trackers, SOPs, and board decks — all pre-filled with your actual company data. No generic placeholders.',
    placement: 'right',
    step: '04',
  },
  {
    target: 'tour-documents',
    title: 'Upload your org knowledge',
    body: 'Add your SOPs, policies, and org structure. The AI uses these to give hyper-specific recommendations tailored to your organization.',
    placement: 'right',
    step: '05',
  },
  {
    target: null,
    title: "You're all set",
    body: 'Create your first company workspace and run your first diagnostic. Your organizational transformation starts now.',
    cta: 'Create my company →',
    emoji: '🚀',
    isFinish: true,
  },
];

function getTargetRect(tourKey) {
  const el = document.querySelector('[data-tour="' + tourKey + '"]');
  if (!el) return null;
  const r = el.getBoundingClientRect();
  return { top: r.top, left: r.left, width: r.width, height: r.height };
}

function tooltipStyle(rect, placement) {
  const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768;

  if (isMobile) {
    // On mobile the drawer itself takes up most of the screen width, so a
    // side-positioned tooltip ("right of the target") has nowhere to fit —
    // it just runs off the right edge. Anchor to the bottom of the screen
    // instead, full-width minus padding, which works regardless of where
    // the spotlighted item sits.
    return {
      position: 'fixed',
      left: 12, right: 12, bottom: 16,
      top: 'auto',
    };
  }

  if (!rect) {
    return {
      position: 'fixed',
      top: '50%', left: '50%',
      transform: 'translate(-50%,-50%)',
    };
  }
  const gap = 18;
  const pad = 20;
  const tooltipH = 280;
  const tooltipW = 340;

  if (placement === 'right') {
    let top = rect.top + rect.height / 2 - tooltipH / 2;
    top = Math.max(pad, Math.min(window.innerHeight - tooltipH - pad, top));
    return { position: 'fixed', top, left: rect.left + rect.width + gap };
  }
  if (placement === 'left') {
    let top = rect.top + rect.height / 2 - tooltipH / 2;
    top = Math.max(pad, Math.min(window.innerHeight - tooltipH - pad, top));
    return { position: 'fixed', top, left: rect.left - tooltipW - gap };
  }
  if (placement === 'bottom') {
    return { position: 'fixed', top: rect.top + rect.height + gap, left: Math.max(pad, rect.left) };
  }
  return { position: 'fixed', top: rect.top - tooltipH - gap, left: Math.max(pad, rect.left) };
}

function Arrow({ placement }) {
  const s = { position: 'absolute', width: 0, height: 0, border: '9px solid transparent' };
  if (placement === 'right') return <div style={{ ...s, borderRightColor: 'var(--bg-surface)', left: -18, top: '50%', transform: 'translateY(-50%)' }} />;
  if (placement === 'left')  return <div style={{ ...s, borderLeftColor:  'var(--bg-surface)', right: -18, top: '50%', transform: 'translateY(-50%)' }} />;
  if (placement === 'bottom')return <div style={{ ...s, borderBottomColor:'var(--bg-surface)', top: -18, left: 24 }} />;
  return                            <div style={{ ...s, borderTopColor:   'var(--bg-surface)', bottom: -18, left: 24 }} />;
}

export default function OnboardingTour({ onComplete, setMobileOpen }) {
  const navigate = useNavigate();
  const [step, setStep]       = useState(0);
  const [rect, setRect]       = useState(null);
  const [mounted, setMounted] = useState(false);
  const [leaving, setLeaving] = useState(false);

  const current = STEPS[step];
  const numSteps = STEPS.filter(s => s.step).length;

  const isMobile = () => window.innerWidth <= 768;

  const measure = useCallback(() => {
    if (!current?.target) {
      // Center-modal step (welcome/finish) — make sure the mobile drawer is closed
      // so it doesn't sit open and unexplained behind the tour overlay.
      if (isMobile()) setMobileOpen?.(false);
      setRect(null);
      return;
    }
    if (isMobile()) {
      // The target lives inside the slide-out drawer, which is off-screen
      // (translateX(-100%)) until opened. Open it, then wait for the CSS
      // transition to finish before measuring its real on-screen position —
      // measuring immediately would capture the pre-animation, off-screen rect.
      setMobileOpen?.(true);
      setTimeout(() => setRect(getTargetRect(current.target)), 280);
    } else {
      setRect(getTargetRect(current.target));
    }
  }, [current?.target, setMobileOpen]);

  useEffect(() => {
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [measure]);

  useEffect(() => {
    const t = requestAnimationFrame(() => setMounted(true));
    return () => cancelAnimationFrame(t);
  }, []);

  useEffect(() => {
    if (current?.target) {
      const el = document.querySelector('[data-tour="' + current.target + '"]');
      el?.scrollIntoView?.({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    }
  }, [step, current?.target]);

  const done = (cb) => {
    setLeaving(true);
    setTimeout(cb, 260);
  };

  const finish = () => {
    localStorage.setItem('onboarding_complete', '1');
    // Absolute path — this component renders outside the <Routes> tree (as a
    // sibling, not inside any matched <Route>), so relative navigation here
    // depends on React Router's exact resolution context rather than the
    // visible URL. An absolute path removes that ambiguity regardless of
    // where this component is ever mounted from.
    done(() => { onComplete?.(); navigate('/companies'); });
  };

  const skip = () => {
    localStorage.setItem('onboarding_complete', '1');
    done(() => onComplete?.());
  };

  const next = () => { if (step === STEPS.length - 1) finish(); else setStep(s => s + 1); };
  const back = () => { if (step > 0) setStep(s => s - 1); };

  const opacity = mounted && !leaving ? 1 : 0;
  const pos = tooltipStyle(rect, current.placement);
  const isCenter = !current.target;
  const isFirst = step === 0;
  const isLast  = step === STEPS.length - 1;
  const isMobileView = typeof window !== 'undefined' && window.innerWidth <= 768;

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 10000,
      opacity, transition: 'opacity 0.26s ease',
      pointerEvents: opacity < 0.1 ? 'none' : 'auto',
    }}>
      {/* Overlay strategy:
          - Sidebar targets: dim only the main content (right side), sidebar stays visible
          - Center modals: soft full-screen dim */}

      {/* Left overlay — only shown for non-sidebar targets or center modals */}
      {!rect && (
        <div
          style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.6)', cursor: 'pointer' }}
          onClick={skip}
        />
      )}

      {/* When targeting sidebar: dim only the right/main area, leave sidebar clear */}
      {rect && (
        <>
          {/* Right content area overlay */}
          <div
            style={{
              position: 'fixed',
              top: 0, left: rect.left + rect.width + 4, /* starts right after sidebar */
              right: 0, bottom: 0,
              background: 'rgba(0,0,0,0.72)', cursor: 'pointer',
            }}
            onClick={skip}
          />
          {/* Top overlay above the spotlight item */}
          <div
            style={{
              position: 'fixed',
              top: 0, left: 0,
              width: rect.left + rect.width + 4,
              height: Math.max(0, rect.top - 7),
              background: 'rgba(0,0,0,0.55)', cursor: 'pointer',
            }}
            onClick={skip}
          />
          {/* Bottom overlay below the spotlight item */}
          <div
            style={{
              position: 'fixed',
              top: rect.top + rect.height + 7, left: 0,
              width: rect.left + rect.width + 4,
              bottom: 0,
              background: 'rgba(0,0,0,0.55)', cursor: 'pointer',
            }}
            onClick={skip}
          />
        </>
      )}

      {/* Spotlight ring around targeted element */}
      {rect && (
        <div style={{
          position: 'fixed',
          top:    rect.top    - 5,
          left:   rect.left   - 5,
          width:  rect.width  + 10,
          height: rect.height + 10,
          borderRadius: 10,
          border: '2px solid #3B82F6',
          boxShadow: '0 0 12px rgba(59,130,246,0.5)',
          zIndex: 1, pointerEvents: 'none',
          transition: 'all 0.3s cubic-bezier(0.4,0,0.2,1)',
        }}>
          <div style={{
            position: 'absolute', inset: -5, borderRadius: 14,
            border: '2px solid rgba(59,130,246,0.4)',
            animation: 'ob-ring 2s ease-in-out infinite',
          }} />
        </div>
      )}

      {/* Tooltip */}
      <div style={{
        ...pos,
        width: isMobileView ? 'auto' : (isCenter ? 420 : 340),
        background: 'var(--bg-surface)',
        border: '1px solid var(--border-2)',
        borderRadius: 16,
        boxShadow: '0 32px 80px rgba(0,0,0,0.5), 0 8px 24px rgba(0,0,0,0.4)',
        zIndex: 2, overflow: 'hidden',
        transition: 'top 0.3s ease, left 0.3s ease',
        maxWidth: isMobileView ? 'none' : 'calc(100vw - 40px)',
      }}>
        {/* Blue top bar */}
        <div style={{ height: 4, background: 'linear-gradient(90deg,#2563EB,#60A5FA)' }} />

        {/* Directional arrow only makes sense when the tooltip is positioned
            beside its target (desktop). On mobile it's a bottom sheet with
            no fixed point to arrow toward. */}
        {rect && !isMobileView && <Arrow placement={current.placement} />}

        <div style={{ padding: isCenter ? '28px 28px 22px' : '20px 22px 18px' }}>

          {/* Emoji for welcome/finish */}
          {(current.isWelcome || current.isFinish) && (
            <div style={{ fontSize: 44, textAlign: 'center', marginBottom: 16, lineHeight: 1 }}>
              {current.emoji}
            </div>
          )}

          {/* Step badge */}
          {current.step && (
            <div style={{
              display: 'inline-flex', alignItems: 'center',
              fontFamily: 'var(--ff-mono)', fontSize: 10, fontWeight: 700,
              color: '#2563EB', background: 'rgba(37,99,235,0.08)',
              border: '1px solid rgba(37,99,235,0.25)',
              padding: '3px 10px', borderRadius: 20, marginBottom: 12, letterSpacing: 1,
            }}>
              STEP {current.step} / {numSteps}
            </div>
          )}

          {/* Title */}
          <div style={{
            fontSize: isCenter ? 21 : 15,
            fontWeight: 700, color: 'var(--text)', lineHeight: 1.3,
            marginBottom: 10,
            textAlign: isCenter ? 'center' : 'left',
          }}>
            {current.title}
          </div>

          {/* Body */}
          <div style={{
            fontSize: 13.5, color: 'var(--text-3)', lineHeight: 1.65,
            marginBottom: 20,
            textAlign: isCenter ? 'center' : 'left',
          }}>
            {current.body}
          </div>

          {/* Progress dots */}
          <div style={{ display: 'flex', justifyContent: 'center', gap: 5, marginBottom: 18 }}>
            {STEPS.map((_, i) => (
              <div key={i} style={{
                height: 5, borderRadius: 3,
                width: i === step ? 22 : 5,
                background: i === step ? '#2563EB' : i < step ? 'rgba(37,99,235,0.4)' : 'var(--border-2)',
                transition: 'all 0.25s ease',
              }} />
            ))}
          </div>

          {/* Buttons */}
          <div style={{ display: 'flex', gap: 8 }}>
            {!isFirst && (
              <button
                onClick={back}
                style={{
                  padding: '9px 16px', border: '1px solid var(--border-2)',
                  borderRadius: 8, background: 'transparent', color: 'var(--text-3)',
                  cursor: 'pointer', fontSize: 13, fontFamily: 'inherit', fontWeight: 500,
                }}
              >
                ← Back
              </button>
            )}
            <button
              onClick={next}
              style={{
                flex: 1, padding: '10px 18px',
                background: '#2563EB', border: '1px solid #2563EB',
                borderRadius: 8, color: '#fff',
                cursor: 'pointer', fontSize: 14, fontWeight: 700,
                fontFamily: 'inherit',
                boxShadow: '0 2px 10px rgba(37,99,235,0.4)',
                transition: 'background 0.15s, transform 0.15s',
              }}
              onMouseEnter={e => { e.currentTarget.style.background = '#1D4ED8'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = '#2563EB'; e.currentTarget.style.transform = ''; }}
            >
              {current.cta || (isLast ? 'Get started' : 'Next →')}
            </button>
          </div>

          {!current.isFinish && (
            <div style={{ textAlign: 'center', marginTop: 12 }}>
              <button
                onClick={skip}
                style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  fontSize: 11.5, color: 'var(--text-4)', fontFamily: 'inherit',
                  textDecoration: 'underline',
                }}
              >
                Skip tour
              </button>
            </div>
          )}
        </div>
      </div>

      <style>{`
        @keyframes ob-ring {
          0%,100% { transform: scale(1); opacity: 0.5; }
          50% { transform: scale(1.06); opacity: 0.2; }
        }
      `}</style>
    </div>
  );
}
